import React, { useState } from "react";
import dayjs from "dayjs";
import { formatPrice, itemCategoryLabel, sellerName } from "./itemFormat";

/**
 * One tile in the Marketplace grid: photo, category, title, price, seller.
 * The whole tile is a button that opens the full listing (ItemDetail).
 */
function ItemCard({ item, isOwner, onOpen }) {
  const firstPhoto = item.image_urls && item.image_urls.length > 0 ? item.image_urls[0] : null;
  // A photo that fails to load falls back to the "No photo" placeholder.
  const [brokenPhoto, setBrokenPhoto] = useState(null);
  const photo = firstPhoto && firstPhoto !== brokenPhoto ? firstPhoto : null;
  const photoCount = item.image_urls ? item.image_urls.length : 0;
  const price = formatPrice(item.price);

  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      aria-label={`${item.title}${item.is_sold ? " (sold)" : ""} — open listing`}
      className={
        "group flex flex-col text-left w-full h-full rounded-[12px] overflow-hidden border " +
        "border-dupontGold/30 dark:border-darkGold/20 bg-white dark:bg-darkCard " +
        "hover:border-dupontGold dark:hover:border-darkGold/70 " +
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dupontGold " +
        "transition-colors duration-200 motion-reduce:transition-none cursor-pointer"
      }
    >
      {/* The whole photo, never cropped, matted on cream in a same-size frame
          (Sam, 2026-09-27: cropping cut off faces and text). Nothing sits on it. */}
      <div className="relative w-full aspect-[4/3] bg-dupontCreamDk/70 dark:bg-darkElevated border-b border-dupontGold/20 dark:border-darkGold/10">
        {photo ? (
          <img
            src={photo}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setBrokenPhoto(photo)}
            className={"absolute inset-0 w-full h-full object-contain p-[10px] " + (item.is_sold ? "grayscale-[70%] opacity-75" : "")}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-y-[8px] text-dupontGold/60 dark:text-darkGold/50">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4.5" width="18" height="15" rx="1.5" />
              <circle cx="8.5" cy="9.5" r="1.6" />
              <path d="M21 16l-5-5-8.5 8.5" />
            </svg>
            <span className="font-body text-[13px] italic text-dupontGray dark:text-darkTextSecondary">No photo</span>
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 px-[18px] pt-[16px] pb-[18px]">
        <div className="flex flex-row items-baseline justify-between gap-x-[10px] min-w-0">
          <span className="truncate text-[12px] font-semibold uppercase tracking-[0.1em] text-dupontGoldDk dark:text-darkGold">
            {itemCategoryLabel(item)}
          </span>
          {photoCount > 1 && (
            <span className="shrink-0 whitespace-nowrap text-[12px] font-body text-dupontGray dark:text-darkTextSecondary">
              {photoCount} photos
            </span>
          )}
        </div>
        <span className="mt-[8px] font-display font-bold text-[19px] leading-snug text-dupontBlue dark:text-darkText line-clamp-2">
          {item.title}
        </span>

        {/* Price and seller sit on the card's bottom edge, so a row of cards lines up. */}
        <div className="mt-auto pt-[16px] flex flex-row flex-wrap items-baseline gap-x-[10px]">
          {price ? (
            <span className={"font-display font-bold text-[20px] " + (item.is_sold ? "text-dupontGray dark:text-darkTextSecondary" : "text-dupontBlue dark:text-darkGold")}>
              <span className={item.is_sold ? "line-through" : ""}>{price}</span>
              {item.price_unit && (
                <span className="ml-[4px] text-[14px] font-body font-normal text-dupontGray dark:text-darkTextSecondary">
                  {item.price_unit}
                </span>
              )}
            </span>
          ) : (
            <span className="font-body italic text-[16px] text-dupontGray dark:text-darkTextSecondary">
              Make an offer
            </span>
          )}
          {item.is_sold && (
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-dupontBlue dark:text-darkText">
              Sold
            </span>
          )}
        </div>
        <span className="mt-[4px] flex flex-row min-w-0 text-[14px] font-body text-dupontGray dark:text-darkTextSecondary">
          {isOwner ? (
            <span className="font-semibold text-dupontGoldDk dark:text-darkGold whitespace-nowrap">Your listing</span>
          ) : (
            <span className="truncate">{sellerName(item)}</span>
          )}
          {item.created_at && (
            <span className="shrink-0 whitespace-nowrap">&nbsp;&middot; {dayjs(item.created_at).format("MMM D")}</span>
          )}
        </span>
      </div>
    </button>
  );
}

export default ItemCard;
